import {
  MULTI_BUILD_COST,
  MULTI_BUILD_REQUIRED_COINS,
  TECH_CARD_DEFINITIONS,
  TECH_COIN_COST,
  UNIT_DEFINITIONS,
} from "../data";
import type {
  BuildMultiAction,
  BuyTechCardAction,
  BuyTechCoinAction,
  Coordinate,
  EndPhaseAction,
  GameLogEntry,
  GameState,
  MoveUnitAction,
  PlayerId,
  PlayerState,
  ProduceUnitAction,
  RevealTechCardAction,
  SurrenderAction,
  UnitInstance,
} from "../types";
import {
  calculateIncome,
  cellKey,
  getOpponent,
  getPlayer,
  getPrimaryHomeTile,
  getUnitsAt,
  getWinnerByControl,
  hasRevealedTech,
  resolveUnitStats,
} from "../utils";

interface Combatant {
  readonly unit: UnitInstance;
  hp: number;
}

function updatePlayer(
  state: GameState,
  playerId: PlayerId,
  updater: (player: PlayerState) => PlayerState,
): readonly [PlayerState, PlayerState] {
  return state.players.map((player) =>
    player.id === playerId ? updater(player) : player,
  ) as [PlayerState, PlayerState];
}

function appendLog(state: GameState, message: string): readonly GameLogEntry[] {
  return [...state.log, { turn: state.turn, phase: state.phase, message }];
}

function unitLabel(unit: UnitInstance): string {
  return UNIT_DEFINITIONS[unit.typeId].name;
}

function maxHp(state: GameState, unit: UnitInstance): number {
  return resolveUnitStats(state, unit).hp;
}

export function applyMoveUnit(
  state: GameState,
  action: MoveUnitAction,
): GameState {
  const unit = state.units.find((candidate) => candidate.id === action.unitId);
  const nextUnits = state.units.map((candidate) =>
    candidate.id === action.unitId
      ? { ...candidate, position: action.to }
      : candidate,
  );
  return {
    ...state,
    units: nextUnits,
    log: appendLog(
      state,
      unit
        ? `${unitLabel(unit)} moved to (${action.to.x + 1}, ${action.to.y + 1}).`
        : "Unit moved.",
    ),
  };
}

export function applyProduceUnit(
  state: GameState,
  action: ProduceUnitAction,
): GameState {
  const player = getPlayer(state, action.playerId);
  if (!player) return state;
  const definition = UNIT_DEFINITIONS[action.unitTypeId];
  const worker = action.workerUnitId
    ? state.units.find((unit) => unit.id === action.workerUnitId)
    : undefined;
  const position = definition.structure
    ? (worker?.position ?? getPrimaryHomeTile(state, player.id).position)
    : getPrimaryHomeTile(state, player.id).position;
  const id = `${player.id}-unit-${state.nextUnitSequence}`;
  const unit: UnitInstance = {
    id,
    ownerId: player.id,
    typeId: action.unitTypeId,
    hp: resolveUnitStats(state, {
      id,
      ownerId: player.id,
      typeId: action.unitTypeId,
      hp: definition.hp,
      position,
    }).hp,
    position,
  };

  return {
    ...state,
    players: updatePlayer(state, player.id, (candidate) => ({
      ...candidate,
      gold: candidate.gold - definition.cost,
    })),
    units: [...state.units, unit],
    nextUnitSequence: state.nextUnitSequence + 1,
    log: appendLog(state, `${definition.name} produced.`),
  };
}

export function applyBuildMulti(
  state: GameState,
  action: BuildMultiAction,
): GameState {
  const key = cellKey(action.at);
  const current = state.constructionByCell[key] ?? 0;
  const nextCoins = Math.min(MULTI_BUILD_REQUIRED_COINS, current + 1);
  return {
    ...state,
    players: updatePlayer(state, action.playerId, (player) => ({
      ...player,
      gold: player.gold - MULTI_BUILD_COST,
    })),
    constructionByCell: {
      ...state.constructionByCell,
      [key]: nextCoins,
    },
    log: appendLog(
      state,
      `M construction advanced to ${nextCoins}/${MULTI_BUILD_REQUIRED_COINS}.`,
    ),
  };
}

export function applyBuyTechCoin(
  state: GameState,
  action: BuyTechCoinAction,
): GameState {
  return {
    ...state,
    players: updatePlayer(state, action.playerId, (player) => ({
      ...player,
      gold: player.gold - TECH_COIN_COST,
      techCoins: player.techCoins + 1,
    })),
    turnFlags: {
      ...state.turnFlags,
      boughtTechCoinPlayerIds: [
        ...state.turnFlags.boughtTechCoinPlayerIds,
        action.playerId,
      ],
    },
    log: appendLog(state, "Tech coin purchased."),
  };
}

export function applyBuyTechCard(
  state: GameState,
  action: BuyTechCardAction,
): GameState {
  const definition = TECH_CARD_DEFINITIONS[action.techCardId];
  const cost = definition.grade === "mid" ? 2 : 3;
  return {
    ...state,
    players: updatePlayer(state, action.playerId, (player) => ({
      ...player,
      techCoins: player.techCoins - cost,
      ownedTech: [
        ...player.ownedTech,
        {
          id: action.techCardId,
          revealed: false,
          purchasedTurn: state.turn,
        },
      ],
    })),
    log: appendLog(state, `${definition.name} purchased.`),
  };
}

export function applyRevealTechCard(
  state: GameState,
  action: RevealTechCardAction,
): GameState {
  const definition = TECH_CARD_DEFINITIONS[action.techCardId];
  const players = updatePlayer(state, action.playerId, (player) => {
    const revealedPlayer = {
      ...player,
      ownedTech: player.ownedTech.map((tech) =>
        tech.id === action.techCardId ? { ...tech, revealed: true } : tech,
      ),
    };
    if (action.techCardId === "SB_MONEY_RAIN") {
      return { ...revealedPlayer, gold: revealedPlayer.gold + 10 };
    }
    return revealedPlayer;
  });

  const constructionByCell =
    action.techCardId === "SB_GOLDEN_TEMPLE" && action.target
      ? {
          ...state.constructionByCell,
          [cellKey(action.target)]: MULTI_BUILD_REQUIRED_COINS,
        }
      : state.constructionByCell;

  return {
    ...state,
    players,
    constructionByCell,
    log: appendLog(state, `${definition.name} revealed.`),
  };
}

function nextPhaseAfterEnd(state: GameState): GameState {
  if (state.phase === "MOVE") {
    return {
      ...state,
      phase: "PRODUCE",
      log: appendLog(state, "Movement ended."),
    };
  }
  if (state.phase === "PRODUCE") {
    return {
      ...state,
      phase: "COMBAT",
      log: appendLog(state, "Production ended."),
    };
  }
  if (state.phase === "COMBAT") {
    return processCombat({
      ...state,
      log: appendLog(state, "Combat resolved."),
    });
  }
  if (state.phase === "OCCUPY") {
    return processOccupation({
      ...state,
      log: appendLog(state, "Occupation resolved."),
    });
  }
  if (state.phase === "INCOME") {
    return processIncome({
      ...state,
      log: appendLog(state, "Income collected."),
    });
  }
  return state;
}

export function applyEndPhase(
  state: GameState,
  _action: EndPhaseAction,
): GameState {
  return nextPhaseAfterEnd(state);
}

export function applySurrender(
  state: GameState,
  action: SurrenderAction,
): GameState {
  const opponent = getOpponent(state, action.playerId);
  if (!opponent) return state;
  return {
    ...state,
    phase: "GAME_OVER",
    winnerId: opponent.id,
    victoryCondition: "surrender",
    log: appendLog(state, `${opponent.name} wins by surrender.`),
  };
}

function processOccupation(state: GameState): GameState {
  const nextControl = { ...state.controlByCell };
  for (const tile of state.board) {
    if (!["capture", "home", "multi"].includes(tile.kind)) continue;
    const owners = new Set(
      getUnitsAt(state, tile.position).map((unit) => unit.ownerId),
    );
    if (owners.size === 1) {
      const [ownerId] = [...owners];
      if (ownerId) nextControl[cellKey(tile.position)] = ownerId;
    }
  }

  const occupied = { ...state, controlByCell: nextControl };
  const winner = getWinnerByControl(occupied);
  if (winner) {
    return {
      ...occupied,
      phase: "GAME_OVER",
      winnerId: winner.winnerId,
      victoryCondition: winner.condition,
      log: [
        ...occupied.log,
        {
          turn: occupied.turn,
          phase: "GAME_OVER",
          message: `Victory by ${winner.condition}.`,
        },
      ],
    };
  }
  return { ...occupied, phase: "INCOME" };
}

function processIncome(state: GameState): GameState {
  const income = calculateIncome(state, state.currentPlayerId);
  const currentIndex = state.players.findIndex(
    (player) => player.id === state.currentPlayerId,
  );
  const nextPlayer = state.players[currentIndex === 0 ? 1 : 0];
  if (!nextPlayer) return state;
  return {
    ...state,
    phase: "MOVE",
    currentPlayerId: nextPlayer.id,
    turn: state.turn + 1,
    players: updatePlayer(state, state.currentPlayerId, (player) => ({
      ...player,
      gold: player.gold + income,
    })),
    turnFlags: { boughtTechCoinPlayerIds: [] },
    log: [
      ...state.log,
      {
        turn: state.turn,
        phase: "INCOME",
        message: `${income} gold income collected.`,
      },
    ],
  };
}

function combatPositions(state: GameState): readonly Coordinate[] {
  return state.board
    .map((tile) => tile.position)
    .filter((position) => {
      const owners = new Set(
        getUnitsAt(state, position).map((unit) => unit.ownerId),
      );
      return owners.size > 1;
    });
}

function removeDead(combatants: Combatant[]): Combatant[] {
  return combatants.filter((combatant) => combatant.hp > 0);
}

function pickHighestLevel(
  state: GameState,
  combatants: readonly Combatant[],
): Combatant | undefined {
  return [...combatants].sort(
    (a, b) =>
      resolveUnitStats(state, b.unit).level -
      resolveUnitStats(state, a.unit).level,
  )[0];
}

function pickLowestLevel(
  state: GameState,
  combatants: readonly Combatant[],
): Combatant | undefined {
  return [...combatants].sort(
    (a, b) =>
      resolveUnitStats(state, a.unit).level -
      resolveUnitStats(state, b.unit).level,
  )[0];
}

function damageTarget(target: Combatant | undefined, damage: number): void {
  if (!target || damage <= 0) return;
  target.hp -= damage;
}

function applySpecials(
  state: GameState,
  side: Combatant[],
  enemies: Combatant[],
  summary: string[],
): void {
  const ordered = [...side].sort(
    (a, b) =>
      resolveUnitStats(state, b.unit).level -
      resolveUnitStats(state, a.unit).level,
  );

  for (const combatant of ordered) {
    if (combatant.hp <= 0) continue;
    if (enemies.length === 0) return;
    if (combatant.unit.typeId === "INVOLVE") {
      const target = [...enemies]
        .filter((enemy) => resolveUnitStats(state, enemy.unit).level <= 3)
        .sort(
          (a, b) =>
            resolveUnitStats(state, b.unit).level -
            resolveUnitStats(state, a.unit).level,
        )[0];
      if (target) {
        target.hp = 0;
        summary.push(
          `${unitLabel(combatant.unit)} removed ${unitLabel(target.unit)}.`,
        );
      }
    }
    if (combatant.unit.typeId === "BOMB") {
      const owner = getPlayer(state, combatant.unit.ownerId);
      const maxLevel = hasRevealedTech(owner, "ECSU_ANTI_TANK_BOMB") ? 4 : 2;
      const count = hasRevealedTech(owner, "ECSU_THORNY_ROAD") ? 3 : 1;
      const targets = enemies
        .filter(
          (enemy) => resolveUnitStats(state, enemy.unit).level <= maxLevel,
        )
        .slice(0, count);
      for (const target of targets) {
        damageTarget(target, resolveUnitStats(state, combatant.unit).atk);
      }
      if (targets.length > 0) {
        combatant.hp = 0;
        summary.push(`${unitLabel(combatant.unit)} detonated.`);
      }
    }
    if (combatant.unit.typeId === "LATENCY") {
      const target = pickLowestLevel(state, enemies);
      damageTarget(target, resolveUnitStats(state, combatant.unit).atk);
      if (target) summary.push(`${unitLabel(combatant.unit)} struck first.`);
    }
    if (combatant.unit.typeId === "JOKER") {
      const target = enemies.find(
        (enemy) => resolveUnitStats(state, enemy.unit).level >= 4,
      );
      damageTarget(target, resolveUnitStats(state, combatant.unit).atk);
      if (target)
        summary.push(`${unitLabel(combatant.unit)} hit a high-level target.`);
    }
    const owner = getPlayer(state, combatant.unit.ownerId);
    if (
      combatant.unit.typeId === "SONIC" &&
      hasRevealedTech(owner, "GO_ASSASSINATE")
    ) {
      const target = pickLowestLevel(state, enemies);
      damageTarget(target, resolveUnitStats(state, combatant.unit).atk);
    }
  }
}

function attackValue(
  state: GameState,
  combatant: Combatant,
  side: readonly Combatant[],
  enemies: readonly Combatant[],
  defending: boolean,
): number {
  const stats = resolveUnitStats(state, combatant.unit);
  let atk = stats.atk;
  if (combatant.unit.typeId === "TURTLE" && defending) atk += 1;
  if (
    combatant.unit.typeId === "DEFY" &&
    enemies.every((enemy) => resolveUnitStats(state, enemy.unit).level <= 3)
  ) {
    atk += 1;
  }
  if (
    combatant.unit.typeId === "ANT" &&
    side.filter((ally) => ally.unit.typeId === "ANT").length >= 3
  ) {
    atk = 0;
  }
  if (
    combatant.unit.typeId === "DESTROYER" &&
    side.some((ally) => ally.unit.typeId === "BISHOP")
  ) {
    atk += 2;
  }
  return atk;
}

function attackCount(
  state: GameState,
  combatant: Combatant,
  enemies: readonly Combatant[],
): number {
  const owner = getPlayer(state, combatant.unit.ownerId);
  if (combatant.unit.typeId === "DESTROYER") return 2;
  if (
    combatant.unit.typeId === "TALDI" &&
    hasRevealedTech(owner, "ECSU_FOR_FEDERATION")
  ) {
    return 2;
  }
  if (
    combatant.unit.typeId === "DEFY" &&
    hasRevealedTech(owner, "GO_REVOLUTION_FROM_BELOW") &&
    enemies.every((enemy) => resolveUnitStats(state, enemy.unit).level <= 3)
  ) {
    return 3;
  }
  return 1;
}

function performAttacks(
  state: GameState,
  side: Combatant[],
  enemies: Combatant[],
  defending: boolean,
): void {
  for (const combatant of side) {
    if (combatant.hp <= 0) continue;
    const attacks = attackCount(state, combatant, enemies);
    for (let index = 0; index < attacks; index += 1) {
      const target = pickHighestLevel(
        state,
        enemies.filter((enemy) => enemy.hp > 0),
      );
      if (!target) return;
      const owner = getPlayer(state, target.unit.ownerId);
      const cannotHitLatency =
        target.unit.typeId === "LATENCY" &&
        hasRevealedTech(owner, "GO_DARK_PATH") &&
        resolveUnitStats(state, combatant.unit).level <= 3;
      if (cannotHitLatency) continue;
      damageTarget(
        target,
        attackValue(state, combatant, side, enemies, defending),
      );
    }
  }
}

function processBanditSteal(
  state: GameState,
  survivors: readonly UnitInstance[],
  enemiesBefore: readonly UnitInstance[],
): GameState {
  let next = state;
  for (const bandit of survivors.filter((unit) => unit.typeId === "BANDIT")) {
    const opponent = getOpponent(next, bandit.ownerId);
    if (!opponent) continue;
    const highestEnemyLevel = Math.max(
      0,
      ...enemiesBefore.map((unit) => resolveUnitStats(next, unit).level),
    );
    const stolen = Math.min(opponent.gold, highestEnemyLevel);
    if (stolen <= 0) continue;
    const withOpponentGold = updatePlayer(next, opponent.id, (player) => ({
      ...player,
      gold: player.gold - stolen,
    }));
    next = {
      ...next,
      players: updatePlayer(
        { ...next, players: withOpponentGold },
        bandit.ownerId,
        (player) => ({
          ...player,
          gold: player.gold + stolen,
        }),
      ),
    };
  }
  return next;
}

function revealTechCardsForPlayers(
  state: GameState,
  playerIds: ReadonlySet<PlayerId>,
): GameState {
  if (playerIds.size === 0) return state;
  return {
    ...state,
    players: state.players.map((player) =>
      playerIds.has(player.id)
        ? {
            ...player,
            ownedTech: player.ownedTech.map((tech) => ({
              ...tech,
              revealed: true,
            })),
          }
        : player,
    ) as [PlayerState, PlayerState],
  };
}

function resolveCombatAtPosition(
  state: GameState,
  position: Coordinate,
): GameState {
  const attackerId = state.currentPlayerId;
  const defenderId = getOpponent(state, attackerId)?.id;
  if (!defenderId) return state;

  let attackers = getUnitsAt(state, position)
    .filter((unit) => unit.ownerId === attackerId)
    .map((unit) => ({ unit, hp: unit.hp }));
  let defenders = getUnitsAt(state, position)
    .filter((unit) => unit.ownerId === defenderId)
    .map((unit) => ({ unit, hp: unit.hp }));
  const defenderBefore = defenders.map((combatant) => combatant.unit);
  const attackerBefore = attackers.map((combatant) => combatant.unit);
  const summary: string[] = [];
  const techRevealPlayerIds = new Set<PlayerId>();
  if (attackers.some((combatant) => combatant.unit.typeId === "PRIVE")) {
    techRevealPlayerIds.add(defenderId);
    summary.push("프라이비 revealed enemy tech cards.");
  }
  if (defenders.some((combatant) => combatant.unit.typeId === "PRIVE")) {
    techRevealPlayerIds.add(attackerId);
    summary.push("프라이비 revealed enemy tech cards.");
  }

  applySpecials(state, attackers, defenders, summary);
  defenders = removeDead(defenders);
  attackers = removeDead(attackers);
  applySpecials(state, defenders, attackers, summary);
  defenders = removeDead(defenders);
  attackers = removeDead(attackers);

  let round = 0;
  while (attackers.length > 0 && defenders.length > 0 && round < 10) {
    round += 1;
    performAttacks(state, attackers, defenders, false);
    defenders = removeDead(defenders);
    if (defenders.length === 0) break;
    performAttacks(state, defenders, attackers, true);
    attackers = removeDead(attackers);
  }

  const survivorIds = new Set([
    ...attackers.map((combatant) => combatant.unit.id),
    ...defenders.map((combatant) => combatant.unit.id),
  ]);
  const combatCellIds = new Set(
    getUnitsAt(state, position).map((unit) => unit.id),
  );
  const survivors = [...attackers, ...defenders].map((combatant) => ({
    ...combatant.unit,
    hp: maxHp(state, combatant.unit),
  }));
  const nonCombatUnits = state.units.filter(
    (unit) => !combatCellIds.has(unit.id),
  );
  const afterUnits = [...nonCombatUnits, ...survivors];
  const resultText =
    summary.length > 0
      ? summary.join(" ")
      : `Combat resolved in ${round} round${round === 1 ? "" : "s"}.`;
  const afterCombat = {
    ...state,
    units: afterUnits,
    combatLog: [
      ...state.combatLog,
      {
        turn: state.turn,
        position,
        summary: resultText,
      },
    ],
    log: [
      ...state.log,
      {
        turn: state.turn,
        phase: "COMBAT" as const,
        message: resultText,
      },
    ],
  };
  const survivingAttackers = afterUnits.filter(
    (unit) => survivorIds.has(unit.id) && unit.ownerId === attackerId,
  );
  const survivingDefenders = afterUnits.filter(
    (unit) => survivorIds.has(unit.id) && unit.ownerId === defenderId,
  );
  const afterBandit = processBanditSteal(
    processBanditSteal(afterCombat, survivingAttackers, defenderBefore),
    survivingDefenders,
    attackerBefore,
  );
  return revealTechCardsForPlayers(afterBandit, techRevealPlayerIds);
}

function processCombat(state: GameState): GameState {
  let next = state;
  for (const position of combatPositions(next)) {
    next = resolveCombatAtPosition(next, position);
  }
  return { ...next, phase: "OCCUPY" };
}

export function applySystemOccupation(state: GameState): GameState {
  return processOccupation(state);
}

export function applySystemIncome(state: GameState): GameState {
  return processIncome(state);
}
